import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Globe2, Users, Award, ArrowRight, Target, HeartHandshake } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0";

const VALUES = [
  { key: "integrity", icon: ShieldCheck },
  { key: "precision", icon: Target },
  { key: "care", icon: HeartHandshake },
  { key: "reach", icon: Globe2 },
];

const STATS = [
  { icon: Award, value: "12+", key: "years" },
  { icon: Users, value: "5,000+", key: "clients" },
  { icon: Globe2, value: "40+", key: "countries" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export const AboutPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-12 items-center" data-testid="about-hero">
          <div>
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">{t("aboutPage.eyebrow")}</p>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] text-brand-gradient" data-testid="about-title">
              {t("aboutPage.title")}
            </h1>
            <p className="mt-5 text-base text-slate-300 leading-relaxed max-w-xl">
              {t("aboutPage.intro")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full brand-gradient text-white hover:opacity-90 shadow-xl shadow-venka-blue/25" data-testid="about-cta-apply">
                <Link to="/apply">{t("aboutPage.ctaApply")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 text-slate-100 hover:bg-white/5" data-testid="about-cta-contact">
                <Link to="/contact">{t("aboutPage.ctaContact")}</Link>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 brand-gradient-soft blur-3xl rounded-full" />
            <img src={ABOUT_IMG} alt="VENKA team" className="relative rounded-3xl border border-white/10 object-cover w-full h-[420px] shadow-2xl shadow-black/40" />
          </div>
        </section>

        {/* Stats */}
        <section className="border-y border-white/10 bg-card/40" data-testid="about-stats">
          <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="py-10 px-6 flex items-center gap-4" data-testid={`about-stat-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient text-white"><Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="font-serif text-3xl text-white">{s.value}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">{t(`aboutPage.stats.${s.key}`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Values */}
        <section className="mx-auto max-w-7xl px-6 py-20" data-testid="about-values">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">{t("aboutPage.valuesEyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">{t("aboutPage.valuesTitle")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.key} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
                  className="rounded-2xl border border-white/10 bg-card p-6" data-testid={`about-value-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-venka-blue/15 text-venka-green-light"><Icon className="h-5 w-5" strokeWidth={1.8} /></span>
                  <h3 className="mt-4 font-serif text-lg text-white">{t(`aboutPage.values.${v.key}.title`)}</h3>
                  <p className="mt-2 text-sm text-slate-400">{t(`aboutPage.values.${v.key}.desc`)}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-6 pb-24" data-testid="about-cta-strip">
          <div className="rounded-3xl border border-white/10 brand-gradient p-10 md:p-14 text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white">{t("aboutPage.ctaTitle")}</h2>
            <p className="mt-3 text-white/85 max-w-xl mx-auto">{t("aboutPage.ctaText")}</p>
            <Button asChild size="lg" className="mt-7 rounded-full bg-white text-venka-blue hover:bg-white/90 font-semibold" data-testid="about-bottom-cta">
              <Link to="/apply">{t("aboutPage.ctaButton")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
